import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Search, Settings, LogOut, X, Check, Menu } from 'lucide-react';
import { enqueteurDashboardData } from '../../data/mockData';

const Header = ({ userType, toggleMobileMenu }) => {
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);
  
  // Générer les notifications en fonction du type d'utilisateur
  const getInitialNotifications = () => {
    if (userType === 'enqueteur') {
      // Notifications pour les enquêteurs : nouveaux dossiers en attente
      return enqueteurDashboardData.dossiersEnAttente.map((dossier, index) => ({
        id: dossier.id,
        type: 'nouveau_dossier',
        dossierId: dossier.id,
        dossierNumero: dossier.reference,
        message: `Vous avez reçu un nouveau dossier - ${dossier.numero}, veuillez l'accepter ou le refuser`,
        date: new Date(dossier.dateAttribution),
        read: false
      }));
    } else {
      // Notifications pour l'admin (existantes)
      return [
        {
          id: 1,
          type: 'info',
          message: "Marie Dupont a accepté le dossier REF-CLIENT-6498",
          date: new Date(),
          read: false
        },
        {
          id: 2,
          type: 'info',
          message: "Nouveau dossier attribué : REF-CLIENT-6951",
          date: new Date(Date.now() - 3600000),
          read: false
        }
      ];
    }
  };

  const [notifications, setNotifications] = useState(getInitialNotifications());
  const notificationRef = useRef(null);

  const handleLogout = () => {
    // Rediriger vers la page de login
    navigate('/');
  };

  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
  };

  const markAsRead = (id) => {
    setNotifications(notifications.map(notif => 
      notif.id === id ? { ...notif, read: true } : notif
    ));
  };

  const markAllAsRead = () => {
    setNotifications(notifications.map(notif => ({ ...notif, read: true })));
  };

  const handleNotificationClick = (notification) => {
    if (userType === 'enqueteur' && notification.type === 'nouveau_dossier') {
      // Rediriger vers le dashboard pour gérer l'acceptation/refus
      navigate('/enqueteur');
      setShowNotifications(false);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  // Fermer le dropdown quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const formatDate = (date) => {
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    
    if (minutes < 1) return "À l'instant";
    if (minutes < 60) return `Il y a ${minutes} min`;
    if (hours < 24) return `Il y a ${hours}h`;
    return date.toLocaleDateString('fr-FR');
  };

  return (
    <header className="bg-white shadow-sm border-b border-port-gray-200">
      <div className="flex items-center justify-between px-4 md:px-6 py-4">
        {/* Burger menu button (mobile only) */}
        <button
          onClick={toggleMobileMenu}
          className="lg:hidden p-2 text-port-gray-600 hover:bg-port-gray-100 rounded-lg transition-colors"
          aria-label="Menu"
        >
          <Menu className="h-6 w-6" />
        </button>

        {/* Logo mobile */}
        <div className="lg:hidden">
          <h1 className="text-lg font-bold text-atl-blue-700">
            Provelliance
          </h1>
        </div>

        {/* Search bar */}
        {(userType !== 'enqueteur') && (
        <div className="hidden md:flex flex-1 max-w-lg">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-port-gray-400" />
            <input
              type="text"
              placeholder={userType === 'admin' ? 'Rechercher un client, dossier...' : 'Rechercher un dossier...'}
              className="w-full pl-10 pr-4 py-2 border border-port-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-atl-blue-500 focus:border-atl-blue-500"
            />
          </div>
        </div>
        )}
        {(userType === 'enqueteur') && (
        <div className="hidden md:flex flex-1 max-w-lg">
          <div className="relative w-full">
              <span className="absolute left-3 top-1/2 w-full"> &nbsp; </span>
          </div>
        </div>
        )}

        {/* Right side actions */}
        <div className="flex items-center space-x-2 md:space-x-4">
          {/* Notifications */}
          <div className="relative" ref={notificationRef}>
            <button 
              onClick={toggleNotifications}
              className="relative p-2 text-port-gray-400 hover:text-port-gray-600 hover:bg-port-gray-100 rounded-lg transition-colors"
            >
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full"></span>
              )}
            </button>

            {/* Dropdown notifications */}
            {showNotifications && (
              <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-port-gray-200 z-50">
                {/* Header du dropdown */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-port-gray-200">
                  <h3 className="text-sm font-semibold text-port-gray-900">
                    Notifications {unreadCount > 0 && `(${unreadCount})`}
                  </h3>
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="text-xs text-atl-blue-600 hover:text-atl-blue-700 font-medium"
                    >
                      Tout marquer comme lu
                    </button>
                  )}
                </div>

                {/* Liste des notifications */}
                <div className="max-h-96 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="px-4 py-8 text-center text-port-gray-500">
                      <Bell className="h-8 w-8 mx-auto mb-2 text-port-gray-300" />
                      <p className="text-sm">Aucune notification</p>
                    </div>
                  ) : (
                    notifications.map((notification) => (
                      <div
                        key={notification.id}
                        onClick={() => handleNotificationClick(notification)}
                        className={`px-4 py-3 border-b border-port-gray-100 hover:bg-port-gray-50 transition-colors cursor-pointer ${
                          !notification.read ? 'bg-atl-blue-50' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 pr-2">
                            <p className={`text-sm ${!notification.read ? 'font-medium text-port-gray-900' : 'text-port-gray-700'}`}>
                              {notification.message}
                            </p>
                            <p className="text-xs text-port-gray-500 mt-1">
                              {formatDate(notification.date)}
                            </p>
                          </div>
                          {!notification.read && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification.id);
                              }}
                              className="flex-shrink-0 p-1 text-atl-blue-600 hover:bg-atl-blue-100 rounded transition-colors"
                              title="Marquer comme lu"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Footer du dropdown */}
                {notifications.length > 0 && (
                  <div className="px-4 py-3 bg-port-gray-50 text-center border-t border-port-gray-200">
                    <button className="text-sm text-atl-blue-600 hover:text-atl-blue-700 font-medium">
                      Voir toutes les notifications
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Logout */}
          <button 
            onClick={handleLogout}
            className="p-2 text-port-gray-400 hover:text-port-gray-600 hover:bg-port-gray-100 rounded-lg transition-colors"
            title="Déconnexion"
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
